/**
 * Core data model schemas for the Recurring Value Podcast Admin Portal
 * 
 * This file defines the primary data structures used throughout the application,
 * including database schemas, validation schemas, and TypeScript types.
 */
import { pgTable, uuid, text, varchar, timestamp, jsonb, boolean } from "drizzle-orm/pg-core";
import { z } from "zod";

/**
 * User Profile schema
 * 
 * Represents user profile information stored in the database
 */
export const userProfileSchema = z.object({
  /** Unique database identifier */
  id: z.string().uuid().optional(),
  /** User ID for authentication linking (matches Clerk user ID) */
  user_id: z.string(),
  /** Tenant ID for multi-tenancy */
  tenant_id: z.string().optional().nullable(),
  /** Team ID for team organization */
  team_id: z.string().optional().nullable(),
  /** User's email address */
  email: z.string().email().optional().nullable(),
  /** Display name shown in the application */
  display_name: z.string().optional().nullable(),
  /** User's real/legal name */
  real_name: z.string().optional().nullable(),
  /** Job title or position */
  title: z.string().optional().nullable(),
  /** Contact phone number */
  phone: z.string().optional().nullable(),
  /** User access level */
  access_level: z.string().optional().nullable(),
  /** Whether user has admin privileges */
  is_admin: z.boolean().optional().nullable(),
  /** URL to user's profile picture */
  profile_picture_url: z.string().url().optional().nullable(),
  /** Record creation timestamp */
  created_at: z.string().optional(),
  /** Record last update timestamp */
  updated_at: z.string().optional()
});

/** User profile type derived from the user profile schema */
export type UserProfile = z.infer<typeof userProfileSchema>;

/**
 * User profile update schema
 * 
 * Defines the fields that can be updated by users
 */
export const userProfileUpdateSchema = z.object({
  /** Display name shown in the application */
  display_name: z.string().optional().nullable(),
  /** User's real/legal name */
  real_name: z.string().optional().nullable(),
  /** Job title or position */
  title: z.string().optional().nullable(),
  /** Contact phone number */
  phone: z.string().optional().nullable(),
  /** URL to user's profile picture */
  profile_picture_url: z.string().url().optional().nullable()
});

/** User profile update type derived from the update schema */
export type UserProfileUpdate = z.infer<typeof userProfileUpdateSchema>;

/**
 * User profiles database table schema
 * 
 * Defines the structure for the user_profiles table in the database
 */
export const userProfiles = pgTable('user_profiles', {
  /** Unique identifier for the profile */
  id: uuid('id').primaryKey().defaultRandom(),
  /** User ID for authentication linking */
  user_id: varchar('user_id').notNull().unique(),
  /** Tenant ID for multi-tenancy */
  tenant_id: varchar('tenant_id').notNull(),
  /** Team ID for team organization */
  team_id: varchar('team_id').notNull(),
  /** User's email address */
  email: varchar('email'),
  /** Display name shown in the application */
  display_name: varchar('display_name'),
  /** User's real/legal name */
  real_name: varchar('real_name'),
  /** Job title or position */
  title: varchar('title'),
  /** Contact phone number */
  phone: varchar('phone'),
  /** User access level */
  access_level: text('access_level'),
  /** Whether user has admin privileges */
  is_admin: boolean('is_admin').default(false),
  /** URL to user's profile picture */
  profile_picture_url: text('profile_picture_url'),
  /** Record creation timestamp */
  created_at: timestamp('created_at').notNull().defaultNow(),
  /** Record last update timestamp */
  updated_at: timestamp('updated_at').notNull().defaultNow()
});

/**
 * Location schema
 * 
 * Represents geographical location data for guests, team members, and clients
 */
export const locationSchema = z.object({
  /** Latitude coordinate */
  lat: z.number(),
  /** Longitude coordinate */
  lng: z.number(),
  /** Location name or address */
  name: z.string(),
  /** Optional city name */
  city: z.string().optional(),
  /** Optional country name */
  country: z.string().optional()
});

/** Location type derived from the location schema */
export type Location = z.infer<typeof locationSchema>;

/**
 * Team member schema
 * 
 * Represents a team member associated with a guest
 */
export const teamMemberSchema = z.object({
  /** Team member's full name */
  name: z.string(),
  /** Optional job title */
  title: z.string().optional(),
  /** Team member's geographical location */
  location: locationSchema
});

/** Team member type derived from the team member schema */
export type TeamMember = z.infer<typeof teamMemberSchema>;

/**
 * Client schema
 * 
 * Represents a client associated with a guest
 */
export const clientSchema = z.object({
  /** Client's name */
  name: z.string(),
  /** Client's geographical location */
  location: locationSchema
});

/** Client type derived from the client schema */
export type Client = z.infer<typeof clientSchema>;

/**
 * Social media schema
 * 
 * Represents social media profiles for a guest
 */
export const socialMediaSchema = z.object({
  /** LinkedIn profile URL */
  linkedin: z.string().optional(),
  /** Twitter/X profile URL */
  twitter: z.string().optional(),
  /** Personal or company website URL */
  website: z.string().optional()
});

/** Social media type derived from the social media schema */
export type SocialMedia = z.infer<typeof socialMediaSchema>;

/**
 * Guest status enum
 * 
 * Represents the current status of a podcast guest
 */
export const guestStatusEnum = z.enum([
  /** Guest has been added but not yet confirmed */
  'pending',
  /** Guest has confirmed their participation */
  'confirmed',
  /** Episode with guest has been recorded */
  'recorded',
  /** Episode with guest has been published */
  'published'
]);

/** Guest status type derived from the guest status enum */
export type GuestStatus = z.infer<typeof guestStatusEnum>;

/**
 * Target Guest status enum
 * 
 * Represents the current status of a potential podcast guest
 */
export const targetGuestStatusEnum = z.enum([
  /** Target guest has been identified but not yet contacted */
  'pending',
  /** Initial outreach has been made to target guest */
  'contacted',
  /** Target guest has expressed interest in participating */
  'interested',
  /** Recording has been scheduled with target guest */
  'scheduled',
  /** Target guest has been converted to a confirmed guest */
  'converted',
  /** Target guest has declined to participate */
  'declined'
]);

/** Target guest status type derived from the target guest status enum */
export type TargetGuestStatus = z.infer<typeof targetGuestStatusEnum>;

/**
 * Nomination/Application status enum
 * 
 * Represents the approval status for nominations and applications
 */
export const approvalStatusEnum = z.enum([
  /** Awaiting review and decision */
  'pending',
  /** Approved for further processing */
  'approved',
  /** Rejected and no further action needed */
  'rejected'
]);

/** Approval status type derived from the approval status enum */
export type ApprovalStatus = z.infer<typeof approvalStatusEnum>;

/**
 * Guests database table schema
 * 
 * Defines the structure for the guests table in the database
 */
export const guests = pgTable('guests', {
  /** Unique identifier for the guest */
  id: uuid('id').primaryKey().defaultRandom(),
  /** Guest's full name */
  name: text('name').notNull(),
  /** Guest's email address */
  email: varchar('email', { length: 255 }).notNull(),
  /** Guest's company or organization */
  company: text('company'),
  /** Guest's job title */
  title: text('title'),
  /** Guest's biography or description */
  bio: text('bio'),
  /** Guest's geographical location */
  location: jsonb('location').$type<Location>().notNull(),
  /** Current status in the podcast process */
  status: text('status').$type<GuestStatus>().notNull().default('pending'),
  /** Associated team members */
  team_members: jsonb('team_members').$type<TeamMember[]>(),
  /** Associated clients */
  clients: jsonb('clients').$type<Client[]>(),
  /** Social media profiles */
  social_media: jsonb('social_media').$type<SocialMedia>(),
  /** Episode number if published */
  episode_number: jsonb('episode_number').$type<number>(),
  /** Episode publication date */
  episode_date: text('episode_date'),
  /** Record creation timestamp */
  created_at: timestamp('created_at').notNull().defaultNow(),
  /** Record last update timestamp */
  updated_at: timestamp('updated_at').notNull().defaultNow()
});

/**
 * Guest validation schema
 * 
 * Defines validation rules for guest data
 */
export const guestSchema = z.object({
  /** Unique identifier (optional for new guests) */
  id: z.string().uuid().optional(),
  /** Guest's full name (required) */
  name: z.string().min(1, "Name is required"),
  /** Guest's email address (required, must be valid email) */
  email: z.string().email("Invalid email address"),
  /** Guest's company or organization (optional) */
  company: z.string().optional().nullable(),
  /** Guest's job title (optional) */
  title: z.string().optional().nullable(),
  /** Guest's biography or description (optional) */
  bio: z.string().optional().nullable(),
  /** Guest's geographical location (required) */
  location: locationSchema,
  /** Current status in the podcast process */
  status: guestStatusEnum,
  /** Associated team members (optional) */
  team_members: z.array(teamMemberSchema).optional().nullable(),
  /** Associated clients (optional) */
  clients: z.array(clientSchema).optional().nullable(),
  /** Social media profiles (optional) */
  social_media: socialMediaSchema.optional().nullable(),
  /** Episode number if published (optional) */
  episode_number: z.number().optional().nullable(),
  /** Episode publication date (optional) */
  episode_date: z.string().optional().nullable(),
  /** Record creation timestamp (optional, set by system) */
  created_at: z.date().optional(),
  /** Record last update timestamp (optional, set by system) */
  updated_at: z.date().optional()
});

/** Guest type derived from the guest schema */
export type Guest = z.infer<typeof guestSchema>;

/**
 * Guest input schema for forms
 * 
 * Simplified schema for creating or updating guests, omitting system-managed fields
 */
export const guestInputSchema = guestSchema.omit({ 
  id: true, 
  created_at: true, 
  updated_at: true 
});

/** Guest input type derived from the guest input schema */
export type GuestInput = z.infer<typeof guestInputSchema>;

/**
 * Target Guest validation schema
 * 
 * Defines validation rules for target guest data
 */
export const targetGuestSchema = z.object({
  /** Unique identifier (optional for new target guests) */
  id: z.string().uuid().optional(),
  /** Target guest's full name (required) */
  name: z.string().min(1, "Name is required"),
  /** Target guest's email address (required, must be valid email) */
  email: z.string().email("Invalid email address"),
  /** Target guest's company or organization (optional) */
  company: z.string().optional().nullable(),
  /** Target guest's job title (optional) */
  title: z.string().optional().nullable(),
  /** Target guest's biography or description (optional) */
  bio: z.string().optional().nullable(),
  /** Target guest's geographical location */
  location: locationSchema,
  /** Current outreach status */
  status: targetGuestStatusEnum,
  /** Priority level from 1-5 (optional) */
  priority: z.number().min(1).max(5).optional(),
  /** Additional notes about the target guest (optional) */
  notes: z.string().optional().nullable(),
  /** Record creation timestamp (optional, set by system) */
  created_at: z.date().optional(),
  /** Record last update timestamp (optional, set by system) */
  updated_at: z.date().optional()
});

/** Target guest type derived from the target guest schema */
export type TargetGuest = z.infer<typeof targetGuestSchema>;

/**
 * Nominations database table schema
 * 
 * Defines the structure for the nominations table in the database
 */
export const nominations = pgTable('nominations', {
  /** Unique identifier for the nomination */
  id: uuid('id').primaryKey().defaultRandom(),
  /** Name of the person making the nomination */
  nominator_name: text('nominator_name').notNull(),
  /** Email of the person making the nomination */
  nominator_email: varchar('nominator_email', { length: 255 }).notNull(),
  /** Name of the person being nominated */
  nominee_name: text('nominee_name').notNull(),
  /** Email of the person being nominated */
  nominee_email: varchar('nominee_email', { length: 255 }).notNull(),
  /** Nominee's company or organization */
  nominee_company: text('nominee_company'),
  /** Nominee's job title */
  nominee_title: text('nominee_title'),
  /** Reason for the nomination */
  reason: text('reason'),
  /** Nominee's geographical location */
  location: jsonb('location').$type<Location>(),
  /** Current approval status */
  status: text('status').$type<ApprovalStatus>().notNull().default('pending'),
  /** Record creation timestamp */
  created_at: timestamp('created_at').notNull().defaultNow()
});

/**
 * Nomination validation schema
 * 
 * Defines validation rules for nomination data
 */
export const nominationSchema = z.object({
  /** Unique identifier (optional for new nominations) */
  id: z.string().uuid().optional(),
  /** Name of the person making the nomination (required) */
  nominator_name: z.string().min(1, "Nominator name is required"),
  /** Email of the person making the nomination (required, must be valid email) */
  nominator_email: z.string().email("Invalid nominator email address"),
  /** Name of the person being nominated (required) */
  nominee_name: z.string().min(1, "Nominee name is required"),
  /** Email of the person being nominated (required, must be valid email) */
  nominee_email: z.string().email("Invalid nominee email address"),
  /** Nominee's company or organization (optional) */
  nominee_company: z.string().optional().nullable(),
  /** Nominee's job title (optional) */
  nominee_title: z.string().optional().nullable(),
  /** Reason for the nomination (optional) */
  reason: z.string().optional().nullable(),
  /** Nominee's geographical location (optional) */
  location: locationSchema.optional().nullable(),
  /** Current approval status */
  status: approvalStatusEnum,
  /** Record creation timestamp (optional, set by system) */
  created_at: z.date().optional()
});

/** Nomination type derived from the nomination schema */
export type Nomination = z.infer<typeof nominationSchema>;

/**
 * Applications database table schema
 * 
 * Defines the structure for the applications table in the database
 */
export const applications = pgTable('applications', {
  /** Unique identifier for the application */
  id: uuid('id').primaryKey().defaultRandom(),
  /** Name of the applicant */
  applicant_name: text('applicant_name').notNull(),
  /** Email of the applicant */
  applicant_email: varchar('applicant_email', { length: 255 }).notNull(),
  /** Applicant's company or organization */
  company: text('company'),
  /** Applicant's job title */
  title: text('title'),
  /** Applicant's biography or description */
  bio: text('bio'),
  /** Applicant's geographical location */
  location: jsonb('location').$type<Location>(),
  /** Current approval status */
  status: text('status').$type<ApprovalStatus>().notNull().default('pending'),
  /** Record creation timestamp */
  created_at: timestamp('created_at').notNull().defaultNow()
});

/**
 * Application validation schema
 * 
 * Defines validation rules for application data
 */
export const applicationSchema = z.object({
  /** Unique identifier (optional for new applications) */
  id: z.string().uuid().optional(),
  /** Name of the applicant (required) */
  applicant_name: z.string().min(1, "Applicant name is required"),
  /** Email of the applicant (required, must be valid email) */
  applicant_email: z.string().email("Invalid applicant email address"),
  /** Applicant's company or organization (optional) */
  company: z.string().optional().nullable(),
  /** Applicant's job title (optional) */
  title: z.string().optional().nullable(),
  /** Applicant's biography or description (optional) */
  bio: z.string().optional().nullable(),
  /** Applicant's geographical location (optional) */
  location: locationSchema.optional().nullable(),
  /** Current approval status */
  status: approvalStatusEnum,
  /** Record creation timestamp (optional, set by system) */
  created_at: z.date().optional()
});

/** Application type derived from the application schema */
export type Application = z.infer<typeof applicationSchema>;

/**
 * User Invitation schema
 * 
 * Represents user invitations for team management
 */
export const userInvitationSchema = z.object({
  /** Unique database identifier */
  id: z.string().uuid().optional(),
  /** Email address of the invited user */
  email: z.string().email(),
  /** Role to assign to the user */
  role: z.enum(['admin', 'editor', 'viewer']),
  /** Invitation status */
  status: z.enum(['pending', 'accepted', 'declined', 'revoked', 'expired']).default('pending'),
  /** User ID who sent the invitation */
  invited_by: z.string(),
  /** Token for invitation verification */
  invitation_token: z.string().optional(),
  /** Expiration date for the invitation */
  expires_at: z.string().optional(),
  /** Date when invitation was accepted */
  accepted_at: z.string().optional().nullable(),
  /** Record creation timestamp */
  created_at: z.string().optional(),
  /** Record last update timestamp */
  updated_at: z.string().optional()
});

/**
 * User invitations database table schema
 */
export const userInvitations = pgTable('user_invitations', {
  /** Unique identifier for the invitation */
  id: uuid('id').primaryKey().defaultRandom(),
  /** Email address of the invited user */
  email: varchar('email', { length: 255 }).notNull(),
  /** Role to assign to the user */
  role: text('role').notNull(),
  /** Invitation status */
  status: text('status').notNull().default('pending'),
  /** User ID who sent the invitation */
  invited_by: varchar('invited_by').notNull(),
  /** Token for invitation verification */
  invitation_token: text('invitation_token'),
  /** Expiration date for the invitation */
  expires_at: timestamp('expires_at'),
  /** Date when invitation was accepted */
  accepted_at: timestamp('accepted_at'),
  /** Record creation timestamp */
  created_at: timestamp('created_at').notNull().defaultNow(),
  /** Record last update timestamp */
  updated_at: timestamp('updated_at').notNull().defaultNow()
});

export type UserInvitation = z.infer<typeof userInvitationSchema>;
